import type { ReactNode } from 'react'
import type { Brand, Vcard } from '../../types'
import { Icons } from '../icons'
import { brandInitial } from '../../utils/brand'
import { cn } from '@/ui/lib/utils'

/** BMC-Blau als Fallback, wenn die Brand keine Primärfarbe gesetzt hat. */
const BMC_BLUE = '#4A6CF7'

export interface VcardPreviewProps {
  data: Vcard
  brand: Brand
}

/** Entfernt das Protokoll-Präfix für eine kompaktere Anzeige (vgl. Dummy). */
function stripProtocol(value: string): string {
  return value.replace(/^https?:\/\//, '')
}

function PreviewItem({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  if (!value) return null
  return (
    <div className="flex items-center gap-2.5 py-2.5">
      <span className="grid size-8 flex-none place-items-center rounded-[9px] bg-muted text-muted-foreground">
        {icon}
      </span>
      <div className="min-w-0">
        <div className="text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">{label}</div>
        <div className="truncate text-[13.5px] font-medium text-card-foreground">{value}</div>
      </div>
    </div>
  )
}

/**
 * Live-Vorschau der öffentlichen vCard aus dem aktuellen Formzustand
 * (kontrolliert, kein eigener Fetch). Leere Felder erscheinen nicht,
 * URL-Präfixe werden gekürzt (Spec §10 / Dummy `updatePreview`).
 *
 * Die Kopf-Farbe kommt aus der Brand-Primärfarbe (dynamisch, inline-`style`);
 * Fallback ist BMC-Blau. Layout/Tokens: `theme.css` (Light+Dark). Die Klassen
 * `vcard`/`vname`/`is-inactive` bleiben als stabile Test-Hooks erhalten.
 */
export function VcardPreview({ data, brand }: VcardPreviewProps) {
  const first = data.firstName?.trim() ?? ''
  const last = data.lastName?.trim() ?? ''
  const name = `${first} ${last}`.trim() || 'Vorname Nachname'
  const initials = (first[0] ?? '') + (last[0] ?? '') || '–'
  const accent = brand.primaryColor || BMC_BLUE

  const addr = [
    [data.teams?.address?.street, data.teams?.address?.houseNumber].filter(Boolean).join(' '),
    [data.teams?.address?.zip, data.teams?.address?.city].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ')

  const items = (
    <>
      <PreviewItem icon={Icons.mail} label="E-Mail" value={data.email?.trim() ?? ''} />
      <PreviewItem icon={Icons.phone} label="Telefon" value={data.phone?.trim() ?? ''} />
      <PreviewItem icon={Icons.mobile} label="Mobil" value={data.mobile?.trim() ?? ''} />
      <PreviewItem icon={Icons.chat} label="WhatsApp" value={data.whatsapp?.trim() ?? ''} />
      <PreviewItem icon={Icons.in} label="LinkedIn" value={stripProtocol(data.linkedin?.trim() ?? '')} />
      <PreviewItem icon={Icons.fb} label="Facebook" value={stripProtocol(data.facebook?.trim() ?? '')} />
      <PreviewItem icon={Icons.web} label="Website" value={stripProtocol(data.website?.trim() ?? '')} />
      <PreviewItem icon={Icons.pin} label="Standort" value={addr} />
    </>
  )

  const hasItems = [
    data.email,
    data.phone,
    data.mobile,
    data.whatsapp,
    data.linkedin,
    data.facebook,
    data.website,
    addr,
  ].some((v) => v && v.trim() !== '')

  return (
    <div
      className={cn(
        'vcard overflow-hidden rounded-xl border bg-card shadow-sm',
        !data.active && 'is-inactive opacity-60',
      )}
    >
      <div
        className="relative overflow-hidden px-5 pb-[18px] pt-[22px] text-center text-white"
        style={{ backgroundImage: `linear-gradient(120deg, ${accent}, color-mix(in srgb, ${accent} 55%, #000))` }}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -right-8 -top-8 size-44 rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(255,255,255,.22), transparent 62%)' }}
        />
        <div className="relative z-[1] mx-auto mb-3 grid size-[74px] place-items-center overflow-hidden rounded-full border-2 border-white/25 bg-white/15 text-2xl font-bold text-white">
          {data.profileImageUrl?.trim() ? (
            <img src={data.profileImageUrl} alt="" className="size-full object-cover" />
          ) : (
            initials
          )}
        </div>
        <div className="vname relative z-[1] text-[19px] font-bold">{name}</div>
        <div className="relative z-[1] mt-0.5 text-[13px] text-white/80">{data.role?.trim() || '—'}</div>
        <div className="relative z-[1] mt-[11px] inline-flex items-center gap-[7px] rounded-full border border-white/25 bg-white/15 px-[11px] py-1 text-xs">
          <span
            className="grid size-4 place-items-center rounded-[5px] text-[9px] font-extrabold text-white"
            style={{ background: accent }}
          >
            {brandInitial(brand)}
          </span>
          {brand.brandName}
        </div>
      </div>
      <div className="flex justify-center px-4 pb-2 pt-5">
        <span
          className="inline-flex h-9 items-center gap-2 rounded-full px-4 text-[13px] font-semibold text-white shadow-md"
          style={{ background: accent }}
        >
          {Icons.go} Kontakt speichern
        </span>
      </div>
      <div className="px-4 pb-4 pt-1">
        {hasItems ? (
          <div className="divide-y divide-border">{items}</div>
        ) : (
          <div className="p-4 text-center text-[12.5px] text-muted-foreground">Noch keine Kontaktdaten</div>
        )}
      </div>
    </div>
  )
}
